// src/components/onboarding/AgreementPage.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { useAuth } from "../../context/AuthContext.jsx";
import {
  downloadAgreementPdf,
  getAgreement,
  openBlob,
  responseMessage,
  signAgreement,
} from "../../shared/_services/onboarding.services";
import StarsIcon from "../../styles/StarsIcon";
import { Button, Checkbox, Input, Panel } from "../ui";
import Loader from "../Loader";
import OnboardingLayout from "./OnboardingLayout";
import {
  AGREEMENT_CANCEL_WINDOW,
  AGREEMENT_DOC_META,
  AGREEMENT_DOC_TITLE,
  AGREEMENT_DOWNLOAD,
  AGREEMENT_DOWNLOAD_TOAST,
  AGREEMENT_END_MARKER,
  AGREEMENT_H1,
  AGREEMENT_HELPER_NAME,
  AGREEMENT_HELPER_READ,
  AGREEMENT_HELPER_TICK,
  AGREEMENT_INTRO_1,
  AGREEMENT_INTRO_2,
  AGREEMENT_NAME_LABEL,
  AGREEMENT_NAME_MISMATCH,
  AGREEMENT_REVISION_CHANGED,
  AGREEMENT_SIDE_NEXT,
  AGREEMENT_SIDE_TITLE,
  AGREEMENT_SIGN,
  AGREEMENT_SIGN_NOTE,
  AGREEMENT_SIGNED,
  AGREEMENT_UNLOCKED,
  GENERIC_ERROR,
  LOAD_FAILED,
  READ_ONLY_CONTINUE,
  TRY_AGAIN,
} from "./onboardingCopy";
import {
  civilDay,
  clockTime,
  documentsRailFor,
  foldName,
  fullDate,
  pdfName,
  railLabelFor,
  signableName,
} from "./onboardingFormat";
import { isWrongStage, useStageNavigation } from "./onboardingNav";
import { prefersReducedMotion, useReadToEndGate } from "./useReadToEndGate";

// The reading-progress hairline: the page's only motion, off under
// prefers-reduced-motion (here: it is not drawn).
function ReadingProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setPct(max > 0 ? Math.min(100, (window.scrollY / max) * 100) : 100);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      data-slot="onb-readbar"
      className="tw-sticky tw-top-0 tw-z-10 tw-h-1 tw-bg-divider"
      aria-hidden="true"
    >
      <span
        className="tw-block tw-h-full tw-bg-text-accent"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// The page for one agreement version. Keyed by versionId in AgreementPage, so
// a re-armed version (revision_changed) starts with a fresh gate, tick and name.
function AgreementBody({
  view,
  loadedAt,
  stage,
  resumed,
  notice,
  onRevisionChanged,
  measure,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const go = useStageNavigation();
  const markerRef = useRef(null);
  const [ticked, setTicked] = useState(false);
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [reduced] = useState(prefersReducedMotion);

  const gate = useReadToEndGate({
    markerRef,
    measure,
    layoutKey: `${ticked}|${name}`,
  });
  const nameOk = signableName(name);
  const canSign = gate.reached && ticked && nameOk;
  const helper = !gate.reached
    ? AGREEMENT_HELPER_READ
    : !ticked
      ? AGREEMENT_HELPER_TICK
      : !nameOk
        ? AGREEMENT_HELPER_NAME
        : "";
  const accountName = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`;
  const mismatch = nameOk && foldName(name) !== foldName(accountName);

  // One announcement per unlock transition: the gate latches, so this runs
  // once however the marker was reached.
  useEffect(() => {
    if (gate.reached) setAnnouncement(AGREEMENT_UNLOCKED);
  }, [gate.reached]);

  const sign = async () => {
    if (!canSign) {
      if (!gate.reached && markerRef.current) {
        markerRef.current.scrollIntoView({
          block: "end",
          behavior: reduced ? "auto" : "smooth",
        });
      }
      enqueueSnackbar(helper, { variant: "info" });
      return;
    }
    setSending(true);
    // R151/R161/R172: never the browser's wall clock, which can be
    // arbitrarily wrong in either direction — and never servedAt, which is
    // the first time this version was ever served and can be days old for a
    // returning reader. serverNow is this response's own instant; adding the
    // real (monotonic) elapsed time between the view loading and the gate
    // reaching the end can never land before serverNow, so there is no
    // separate "behind the clock" case to handle.
    const endMarkerReachedAt = new Date(
      Date.parse(view.serverNow) + (gate.reachedAt - loadedAt),
    ).toISOString();
    try {
      const res = await signAgreement({
        documentVersionId: view.versionId,
        typedName: name.normalize("NFC").trim(),
        esignTicked: true,
        endMarkerReachedAt,
        gateMode: gate.gateMode,
      });
      go(res.data);
    } catch (err) {
      if (isWrongStage(err)) {
        go(err.response.data);
        return;
      }
      if (err?.response?.data?.code === "revision_changed") {
        onRevisionChanged();
        return;
      }
      // bad_name, esign_required and bad_marker_time (400s) carry a real
      // sentence worth showing, same as the other three pages. hash_mismatch
      // (409, alongside wrong_stage/revision_changed above) is server-generic
      // already, so it keeps the house line rather than reading its message.
      const message =
        err?.response?.status === 409
          ? GENERIC_ERROR
          : err?.response?.data?.message || GENERIC_ERROR;
      enqueueSnackbar(message, { variant: "error" });
      setSending(false);
    }
  };

  const download = async () => {
    enqueueSnackbar(AGREEMENT_DOWNLOAD_TOAST, { variant: "info" });
    try {
      const res = await downloadAgreementPdf();
      openBlob(res.data, pdfName(AGREEMENT_DOC_TITLE, view.versionLabel));
    } catch (err) {
      enqueueSnackbar((await responseMessage(err)) || LOAD_FAILED, {
        variant: "error",
      });
    }
  };

  const muted = "tw-m-0 tw-text-sm tw-text-text-muted";
  // The signature block sits under the text (the comp's reader flow); the fixed
  // action bar holds the helper, Sign and continue, Download a copy and the
  // next line.
  const signatureBlock = gate.reached && (
    <div data-slot="onb-sigblock" className="tw-flex tw-flex-col tw-gap-4">
      <p
        data-slot="onb-notice"
        className="tw-m-0 tw-rounded-md tw-bg-overlay-success-600/10 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-success dark:tw-bg-overlay-success-300/15"
      >
        {AGREEMENT_UNLOCKED}
      </p>
      <Panel
        as="div"
        data-slot="onb-consent-callout"
        className="tw-border-text-accent tw-p-4"
      >
        <Checkbox
          id="onb-esign"
          checked={ticked}
          onChange={(e) => setTicked(e.target.checked)}
          label={
            <>
              {view.esignWording.label}
              <span data-slot="onb-muted" className="tw-block tw-text-text-muted">
                {view.esignWording.why}
              </span>
            </>
          }
          labelClassName="tw-font-semibold"
        />
      </Panel>
      <div data-slot="onb-field" className="tw-flex tw-flex-col tw-gap-2">
        <label
          htmlFor="onb-typed-name"
          className="tw-m-0 tw-text-sm tw-font-semibold tw-text-text"
        >
          {AGREEMENT_NAME_LABEL}
        </label>
        <Input
          id="onb-typed-name"
          type="text"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {mismatch && (
          <p data-slot="onb-muted" className={muted}>
            {AGREEMENT_NAME_MISMATCH}
          </p>
        )}
      </div>
      <p data-slot="onb-muted" className={muted}>
        {AGREEMENT_SIGN_NOTE}
      </p>
      <p data-slot="onb-muted" className={muted}>
        {AGREEMENT_CANCEL_WINDOW(civilDay(view.today?.date))}
      </p>
    </div>
  );

  const side = (
    <>
      {helper && (
        <p data-slot="onb-muted" className={muted}>
          {helper}
        </p>
      )}
      <Button
        aria-disabled={!canSign || undefined}
        disabled={sending}
        onClick={sign}
      >
        {AGREEMENT_SIGN}
        <StarsIcon name="arrow" className="tw-h-4 tw-w-4 tw-shrink-0" />
      </Button>
      <Button variant="quiet" size="sm" className="tw-ml-auto" onClick={download}>
        <StarsIcon name="download" className="tw-h-4 tw-w-4 tw-shrink-0" />
        {AGREEMENT_DOWNLOAD}
      </Button>
      <p data-slot="onb-muted" className={`${muted} tw-w-full`}>
        {AGREEMENT_SIDE_NEXT}
      </p>
    </>
  );

  return (
    <OnboardingLayout
      railLabel={railLabelFor(stage?.rail)}
      sideTitle={AGREEMENT_SIDE_TITLE}
      side={side}
      sideBody={signatureBlock || undefined}
      flow
      resumed={resumed}
    >
      {!reduced && <ReadingProgress />}
      <div
        className="tw-sr-only"
        role="status"
        aria-live="polite"
        data-testid="agreement-announcer"
      >
        {announcement}
      </div>
      {notice && (
        <p
          data-slot="onb-notice"
          className="tw-m-0 tw-rounded-md tw-bg-overlay-warning-600/10 tw-px-4 tw-py-3 tw-text-sm tw-text-warning dark:tw-bg-overlay-warning-300/15"
          role="alert"
        >
          {notice}
        </p>
      )}
      <AgreementIntro />
      <AgreementText
        view={view}
        markerRef={markerRef}
        onMarkerFocus={gate.onMarkerFocus}
      />
    </OnboardingLayout>
  );
}

function AgreementIntro() {
  return (
    <>
      <h1
        data-slot="onb-h1"
        className="tw-m-0 tw-text-2xl tw-font-bold tw-text-text max-rail:tw-text-xl"
      >
        {AGREEMENT_H1}
      </h1>
      <p data-slot="onb-muted" className="tw-m-0 tw-text-md tw-text-text-muted">
        {AGREEMENT_INTRO_1}
      </p>
      <p data-slot="onb-muted" className="tw-m-0 tw-text-md tw-text-text-muted">
        {AGREEMENT_INTRO_2}
      </p>
    </>
  );
}

function AgreementText({ view, markerRef, onMarkerFocus }) {
  return (
    <article aria-labelledby="onb-agreement-title">
      <div
        data-slot="onb-agreement-head"
        className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-gap-3"
      >
        <h2
          id="onb-agreement-title"
          data-slot="onb-h3"
          className="tw-m-0 tw-text-lg tw-font-bold tw-text-text"
        >
          {AGREEMENT_DOC_TITLE}
        </h2>
        <span
          data-slot="onb-muted"
          className="tw-text-sm tw-font-semibold tw-text-text-muted"
        >
          {AGREEMENT_DOC_META(view.versionLabel, view.pages)}
        </span>
      </div>
      <Panel
        as="div"
        data-slot="onb-agreement"
        className="tw-text-sm"
      >
        {view.sections.map((s, i) => (
          <React.Fragment key={i}>
            <h4 className="tw-m-0 tw-mb-2 tw-mt-4 tw-text-md tw-font-bold tw-text-text first:tw-mt-0">
              {s.heading}
            </h4>
            <p className="tw-m-0 tw-whitespace-pre-wrap tw-text-text">
              {s.body}
            </p>
          </React.Fragment>
        ))}
      <div
        ref={markerRef}
        data-slot="onb-endmark"
        className="tw-mt-4 tw-border-0 tw-border-t tw-border-dashed tw-border-divider tw-pt-3 tw-text-center tw-text-xs tw-font-bold tw-text-text-muted focus:tw-outline focus:tw-outline-2 focus:tw-outline-offset-2 focus:tw-outline-focus-ring"
        tabIndex={markerRef ? 0 : undefined}
        onFocus={onMarkerFocus}
      >
        {AGREEMENT_END_MARKER}
      </div>
      </Panel>
    </article>
  );
}

// After signing: the signed state, read-only; the signature is never
// re-collected. Forward goes to the client's own page and writes nothing. (A
// client past this page with no signature — an account from before SP3 —
// sees the text alone.)
function SignedAgreement({ view, stage, resumed, readOnly }) {
  const go = useStageNavigation();
  const { signed } = view;
  return (
    <OnboardingLayout
      railLabel={railLabelFor(
        readOnly
          ? documentsRailFor("documents_agreement", stage?.stages)
          : stage?.rail,
      )}
      sideTitle={AGREEMENT_SIDE_TITLE}
      resumed={resumed}
      side={
        <>
          {signed && (
            <p className="tw-m-0 tw-text-sm tw-text-text">
              {AGREEMENT_SIGNED({
                date: fullDate(signed.signedAt),
                time: clockTime(signed.signedAt),
                versionLabel: signed.versionLabel,
                typedName: signed.typedName,
              })}
            </p>
          )}
          <Button onClick={() => go(stage)}>
            {READ_ONLY_CONTINUE}
            <StarsIcon name="arrow" className="tw-h-4 tw-w-4 tw-shrink-0" />
          </Button>
        </>
      }
    >
      <AgreementIntro />
      <AgreementText view={view} markerRef={null} onMarkerFocus={undefined} />
    </OnboardingLayout>
  );
}

// /onboarding/agreement — "Before we can talk about your money" (spec § Agreement).
// `measure` is the read-to-end gate's layout seam (tests only).
export default function AgreementPage({
  readOnly = false,
  stage,
  resumed = false,
  measure,
}) {
  const [view, setView] = useState(null);
  // R151/R161: the monotonic instant this view (and its servedAt) was
  // received, paired with the gate's own monotonic reachedAt so the sign
  // body never has to trust the browser's wall clock.
  const [loadedAt, setLoadedAt] = useState(null);
  const [failed, setFailed] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const res = await getAgreement();
      setLoadedAt(performance.now());
      setView(res.data);
    } catch (_) {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <OnboardingLayout
        railLabel={railLabelFor(
          readOnly
            ? documentsRailFor("documents_agreement", stage?.stages)
            : stage?.rail,
        )}
        sideTitle={AGREEMENT_SIDE_TITLE}
      >
        <div role="alert" className="tw-text-sm tw-text-text">
          <span>{LOAD_FAILED}</span>{" "}
          <Button variant="text" size="sm" onClick={load}>
            {TRY_AGAIN}
          </Button>
        </div>
      </OnboardingLayout>
    );
  }
  if (!view) return <Loader />;
  if (view.signed || readOnly) {
    return (
      <SignedAgreement
        view={view}
        stage={stage}
        resumed={resumed}
        readOnly={readOnly}
      />
    );
  }
  return (
    <AgreementBody
      key={view.versionId}
      view={view}
      loadedAt={loadedAt}
      stage={stage}
      resumed={resumed}
      notice={notice}
      measure={measure}
      onRevisionChanged={async () => {
        await load();
        setNotice(AGREEMENT_REVISION_CHANGED);
      }}
    />
  );
}
